// src/hooks/useAlerts.js
import { useMemo, useRef, useEffect, useState } from 'react';
import { useAlertStore } from '@store';
import { useStaleWhileRevalidate } from './offline';
import { fetchNWSAlerts, fetchUSGSGauges } from '@services/alerts';
import { REFRESH }          from '@config';
import { useLowPowerMode } from './useLowPowerMode';
import { getDB }            from '@services/storage/firebase';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { useUserStore } from '@store/userStore';

export function useAlerts(scope = 'zones') {
  const lpm = useLowPowerMode();
  const [firestoreAlerts, setFirestoreAlerts] = useState([]);

  useEffect(() => {
    let unsub;
    (async () => {
      try {
        const { initFirebase } = await import('@services/storage/firebase');
        const { db } = await initFirebase();
        if (!db) return;
        const q = query(
          collection(db, 'alerts'),
          where('status', '==', 'active')
        );
        unsub = onSnapshot(q,
          (snap) => {
            setFirestoreAlerts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
          },
          (err) => console.warn('[useAlerts] Firestore listener error:', err.message)
        );
      } catch (e) {
        console.warn('[useAlerts] Could not start Firestore listener:', e.message);
      }
    })();
    return () => unsub?.();
  }, []);

  // NWS alerts now come from Firestore (pollNWSAlerts Cloud Function every 3min)
  // Client-side fetch disabled to prevent CORS proxy spam
  const nwsSWR = { data: null, loading: false, error: null, reload: () => {} };
  const gaugeSWR = useStaleWhileRevalidate(fetchUSGSGauges, {
    cacheKey:   'usgs_gauges',
    intervalMs: lpm.active ? lpm.refreshIntervals.alerts : REFRESH.alerts,
  });

  const { nwsAlerts: storeAlerts, usgsGauges: storeGauges, alertsVersion, expiryVersion, tickExpiry } = useAlertStore();
  const expiryTimer = useRef(null);

  // Simulated alerts (from simulator) always override — prepend to all sources
  const simAlerts  = (storeAlerts ?? []).filter(a => a.simulated);
  // When sim alerts exist, suppress Firestore alerts — sim always wins
  const baseAlerts = simAlerts.length > 0
    ? []  // suppress real alerts during simulation
    : firestoreAlerts.length > 0 ? firestoreAlerts : (nwsSWR.data ?? storeAlerts ?? []);
  const nwsAlerts  = simAlerts.length > 0
    ? simAlerts  // simulation only — no mixing with real data
    : baseAlerts;
  const usgsGauges = Array.isArray(gaugeSWR.data?.gauges) ? gaugeSWR.data.gauges : Array.isArray(gaugeSWR.data) ? gaugeSWR.data : storeGauges ?? [];

  const zone   = useUserStore((s) => s.zone);
  const zones  = useUserStore((s) => s.zones);
  const activeZones = zones.length > 0 ? zones : (zone ? [zone] : []);

  // Zone → parish mapping for alert relevance
  const ZONE_TO_PARISH = {
    'N.O. East': 'Orleans', 'Lakeview': 'Orleans', 'Gentilly': 'Orleans',
    'Lower 9th Ward': 'Orleans', 'Carrollton': 'Orleans', 'Uptown': 'Orleans',
    'Mid-City': 'Orleans', 'French Quarter': 'Orleans', 'Algiers': 'Orleans',
    'Jefferson Parish': 'Jefferson', 'Metairie': 'Jefferson', 'Kenner': 'Jefferson',
    'St. Bernard Parish': 'St. Bernard', 'St. Tammany Parish': 'St. Tammany',
    'Northshore / Slidell': 'St. Tammany', 'Plaquemines Parish': 'Plaquemines',
    'Belle Chasse': 'Plaquemines', 'Braithwaite': 'Plaquemines', 'Empire': 'Plaquemines',
    'Port Sulphur': 'Plaquemines', 'Buras': 'Plaquemines', 'Venice': 'Plaquemines',
    'Hahnville': 'St. Charles', 'Luling': 'St. Charles', 'Boutte': 'St. Charles',
    'Destrehan': 'St. Charles', 'Norco': 'St. Charles',
    'LaPlace': 'St. John', 'Reserve': 'St. John', 'Edgard': 'St. John',
    'Gretna': 'Jefferson', 'Westwego': 'Jefferson', 'Harvey': 'Jefferson',
    'Marrero': 'Jefferson', 'Terrytown': 'Jefferson', 'Elmwood': 'Jefferson',
    'Chalmette': 'St. Bernard', 'Arabi': 'St. Bernard', 'Meraux': 'St. Bernard',
    'Violet': 'St. Bernard', 'St. Bernard Parish': 'St. Bernard',
    'Hammond': 'Tangipahoa', 'Ponchatoula': 'Tangipahoa', 'Amite': 'Tangipahoa',
    'Covington': 'St. Tammany', 'Mandeville': 'St. Tammany', 'Slidell': 'St. Tammany',
    'Madisonville': 'St. Tammany', 'Pearl River': 'St. Tammany',
    'St. Charles Parish': 'St. Charles', 'St. John Parish': 'St. John',
    'St. Tammany Parish': 'St. Tammany', 'Jefferson Parish': 'Jefferson',
    'Plaquemines Parish': 'Plaquemines', 'St. Bernard Parish': 'St. Bernard',
    // Parish-level zone names (user selects parish directly)
    'St. Charles': 'St. Charles', 'St. John': 'St. John',
    'St. Tammany': 'St. Tammany', 'St. Bernard': 'St. Bernard',
    'St. James': 'St. James', 'St. Mary': 'St. Mary',
    'Jefferson': 'Jefferson', 'Orleans': 'Orleans', 'Plaquemines': 'Plaquemines',
    'Tangipahoa': 'Tangipahoa', 'Livingston': 'Livingston',
    'Ascension': 'Ascension', 'Assumption': 'Assumption',
    'Lafourche': 'Lafourche', 'Terrebonne': 'Terrebonne',
    'Iberville': 'Iberville', 'East Baton Rouge': 'East Baton Rouge',
    'Washington': 'Washington',
    // New Orleans neighborhoods
    'New Orleans': 'Orleans', 'New Orleans East': 'Orleans',
    'Bywater': 'Orleans', 'Marigny': 'Orleans', 'Tremé': 'Orleans',
    'Garden District': 'Orleans', 'Central Business District': 'Orleans',
    // Additional communities
    'Braithwaite': 'Plaquemines', 'Empire': 'Plaquemines', 'Venice': 'Plaquemines',
    'Ama': 'St. Charles', 'Killona': 'St. Charles', 'Taft': 'St. Charles', 'Montz': 'St. Charles',
    'Carrollton': 'Orleans', 'French Quarter': 'Orleans',
    'N.O. East': 'Orleans', 'Elmwood': 'Jefferson',
    'Pearl River': 'St. Tammany',
    'Sorrento': 'Ascension', 'Napoleonville': 'Assumption',
    'Plaquemine': 'Iberville',
    'Lockport': 'Lafourche', 'Cut Off': 'Lafourche',
    'Bogalusa': 'Washington',
    'Westbank': 'Jefferson', 'Poydras': 'St. Bernard',
    // St. Charles communities missing from original map
    'St. Rose': 'St. Charles', 'Paradis': 'St. Charles', 'Ama': 'St. Charles',
    'Killona': 'St. Charles', 'Taft': 'St. Charles', 'Montz': 'St. Charles',
    // Westbank Jefferson
    'Avondale': 'Jefferson', 'Bridge City': 'Jefferson',
    // Additional zones
    'Houma': 'Terrebonne', 'Thibodaux': 'Lafourche', 'Morgan City': 'St. Mary',
    'Gonzales': 'Ascension', 'Prairieville': 'Ascension',
    'Denham Springs': 'Livingston', 'Walker': 'Livingston',
    'Baton Rouge': 'East Baton Rouge',
  };
  const userParishes = [...new Set(activeZones.map(z => ZONE_TO_PARISH[z]).filter(Boolean))];

  const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;

  const activeAlerts = useMemo(() => {
    const now = Date.now();
    return nwsAlerts
      .filter((a) => {
        if (a.simulated) {
          // Bypass zone filter but still respect expiry and future-date
          const exp = a.expiresAt ? new Date(a.expiresAt).getTime() : 0;
          if (exp && exp < Date.now()) return false;  // expired sim alert
          const simIssued = a.issuedAt ? new Date(a.issuedAt).getTime() : 0;
          if (simIssued && simIssued > Date.now() + 5 * 60 * 1000) return false;  // future-dated
          return true;
        }
        if (a.status !== 'active') return false;
        // Filter out alerts older than 3 days
        const issued = a.issuedAt ? new Date(a.issuedAt).getTime() : 0;
        if (issued && issued > now + 5 * 60 * 1000) return false;  // future-dated
        if (issued && (now - issued) > THREE_DAYS_MS) return false;
        // Filter out expired alerts
        const expires = a.expiresAt ? new Date(a.expiresAt).getTime() : 0;
        if (expires && expires < now) return false;
        if (a.status === 'cancelled') return false; // IPAWS cancellation
        // Filter by user's zones — My Zones mode
        if (scope === 'zones' && userParishes.length > 0) {
          const sender = (a.senderName ?? '').toLowerCase();
          const parishes = a.parishes ?? [];

          // Must be from a Louisiana NWS office
          // Jackson MS and other non-LA offices are excluded entirely
          // IPAWS alerts (AMBER, Civil Emergency, 911 outage) bypass the NWS office check
          // They are pre-filtered to Louisiana SAME codes by the fetchIPAWS Cloud Function
          const isIPAWS   = a.source === 'ipaws';
          const isLAOffice = isIPAWS ||
                             sender.includes('new orleans') ||
                             sender.includes('lake charles') ||
                             sender.includes('baton rouge') ||
                             sender.includes('shreveport');
          if (!isLAOffice) return false;

          // Only show alerts for user's exact parishes + immediately adjacent ones
          const ADJACENT_PARISHES = {
            'St. Tammany': ['St. Tammany','Washington','Tangipahoa','St. John','Orleans','Jefferson'],
            'Orleans':     ['Orleans','Jefferson','St. Bernard','Plaquemines','St. Charles','St. John'],
            'Jefferson':   ['Jefferson','Orleans','St. Bernard','Plaquemines','St. Charles'],
            'St. Charles': ['St. Charles','Jefferson','St. John','St. James','Lafourche'],
            'St. Bernard': ['St. Bernard','Orleans','Plaquemines'],
            'Plaquemines': ['Plaquemines','Jefferson','St. Bernard'],
            'St. John':    ['St. John','St. Charles','St. James','Jefferson','St. Tammany'],
          };
          const SE_LA_PARISHES = [...new Set([
            ...userParishes,
            ...userParishes.flatMap(p => ADJACENT_PARISHES[p] ?? []),
          ])];
          // If alert has parish data, check it matches user's parish
          if (parishes.length > 0) {
            const matchesUserParish = parishes.some(p =>
              userParishes.some(up => up.toLowerCase() === p.toLowerCase())
            );
            // For large alerts covering many areas, require exact user parish match
            // This prevents multi-state alerts from bleeding into unaffected zones
            const isLargeAlert = parishes.length > 8;
            // Multi-state alert with no exact parish match → exclude
            const areaText = a.areaDesc ?? a.area ?? '';
            const isMultiState = /Arkansas|Mississippi|Texas|Oklahoma|Alabama/i.test(areaText);
            if ((isLargeAlert || isMultiState) && !matchesUserParish) return false;
            const isSeLA = parishes.some(p => SE_LA_PARISHES.includes(p));
            if (!matchesUserParish && !isSeLA) return false;
          } else {
            // No parish data — must be purely Louisiana with no other states
            const area = (a.area ?? '');
            // Reject if area mentions Texas, Mississippi, or Arkansas counties
            const hasForeignState = /TX|MS|AR|Alabama|Georgia/i.test(area) ||
                                    /Orange|Cameron|Hardin|Newton|Jasper|Sabine/.test(area);
            if (hasForeignState) return false;
            // Check area text for user's adjacent parish names
            const areaLower = area.toLowerCase();
            const hasSeLA = SE_LA_PARISHES.some(p => {
              const pLower = p.toLowerCase();
              const regex = new RegExp('\\b' + pLower.replace(/\./g, '\\.') + '\\b');
              return regex.test(areaLower);
            });
            if (!hasSeLA) return false;
          }
        }
        return true;
      })
      .sort((a, b) => {
        // 1. Zone-matching alerts first
        const aMatch = (a.parishes ?? []).some(p => userParishes.includes(p));
        const bMatch = (b.parishes ?? []).some(p => userParishes.includes(p));
        if (aMatch && !bMatch) return -1;
        if (!aMatch && bMatch) return 1;
        // 2. Then by severity
        const sev = { extreme: 4, severe: 3, moderate: 2, minor: 1 };
        const sevDiff = (sev[b.severity] ?? 0) - (sev[a.severity] ?? 0);
        if (sevDiff !== 0) return sevDiff;
        // 3. Then by most recent
        const aTime = a.issuedAt ? new Date(a.issuedAt).getTime() : 0;
        const bTime = b.issuedAt ? new Date(b.issuedAt).getTime() : 0;
        return bTime - aTime;
      });
  }, [nwsAlerts, storeAlerts, alertsVersion, expiryVersion, userParishes.join(',')]);  // expiryVersion forces recompute on alert expiry


  // ── Cross-source deduplication ───────────────────────────────────
  // Same VTEC code from NWS + IPAWS = one logical alert (lineage, not corroboration).
  // Keep the NWS copy as canonical; attach IPAWS as a delivery path annotation.
  // Also dedup by identical event+senderName+expiresAt when no VTEC present.
  const dedupedAlerts = (() => {
    const seen = new Map(); // vtec|fingerprint → index in result
    const result = [];
    for (const alert of activeAlerts) {
      // correlationId is the canonical dedup key — set at ingestion for both NWS and IPAWS
      // VTEC is used as correlationId by both sources for weather alerts
      // Fallback: event+office+expiry fingerprint for alerts without VTEC
      const corrId = alert.correlationId ?? alert.vtec ?? null;
      const fingerprint = corrId
        ? corrId.trim()
        : `${alert.event}|${(alert.senderName ?? '').split(' ').slice(0,3).join(' ')}|${alert.expiresAt?.slice(0,16) ?? ''}`;

      if (seen.has(fingerprint)) {
        // Duplicate — merge delivery paths onto canonical record
        const idx = seen.get(fingerprint);
        const canonical = result[idx];
        const paths = canonical.deliveryPaths ?? [canonical.source ?? 'nws'];
        const newPath = alert.source ?? 'unknown';
        if (!paths.includes(newPath)) {
          result[idx] = { ...canonical, deliveryPaths: [...paths, newPath] };
        }
      } else {
        seen.set(fingerprint, result.length);
        result.push({ ...alert, deliveryPaths: [alert.source ?? 'nws'] });
      }
    }
    return result;
  })();

  // Schedule expiry timer after activeAlerts computed — fires when nearest expiresAt reached
  useEffect(() => {
    if (expiryTimer.current) clearTimeout(expiryTimer.current);
    const now = Date.now();
    const nextExpiry = nwsAlerts
      .map(a => (a.expiresAt && a.expiresAt !== undefined) ? new Date(a.expiresAt).getTime() : 0)
      .filter(t => t > now)
      .sort((a, b) => a - b)[0];
    if (!nextExpiry) return;
    const delay = nextExpiry - now + 200;
    expiryTimer.current = setTimeout(() => tickExpiry(), delay);
    return () => { if (expiryTimer.current) clearTimeout(expiryTimer.current); };
  }, [nwsAlerts.map(a => a.expiresAt).join(',')]);  // only re-run when expiry times change

  // extremeAlert also recomputes on expiry
  const extremeAlert = useMemo(() => {
    const priority = { extreme: 4, severe: 3, moderate: 2, minor: 1 };
    return activeAlerts
      .filter((a) => a.severity === 'extreme' || a.severity === 'severe')
      .sort((a, b) => (priority[b.severity] ?? 0) - (priority[a.severity] ?? 0))[0] ?? null;
  }, [activeAlerts]);

  const prevExtremeId = useRef(null);
  useEffect(() => {
    const el = document.getElementById('eos-alert-announce');
    if (!extremeAlert) {
      // Alert cleared — reset announcement element
      if (el) el.textContent = '';
      prevExtremeId.current = null;
      return;
    }
    const id = extremeAlert.id ?? extremeAlert.title;
    if (id === prevExtremeId.current) return;
    prevExtremeId.current = id;
    if (el) {
      el.textContent = `Emergency alert: ${extremeAlert.event ?? extremeAlert.title ?? 'Severe weather'} in effect.`;
    }
  }, [extremeAlert]);

  return {
    nwsAlerts,
    usgsGauges,
    activeAlerts: dedupedAlerts,
    extremeAlert,
    loading:     nwsSWR.loading && !nwsAlerts.length,
    isStale:     nwsSWR.isStale || gaugeSWR.isStale,
    error:       nwsSWR.error ?? gaugeSWR.error ?? null,
    lastUpdated: nwsSWR.lastUpdated,
    refresh:     nwsSWR.reload,
  };
}
